import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '@/components/UI/DataTable';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { useOptionBuyingStrategies } from '@/hooks/useOptionBuyingStrategies';
import { STRATEGY_SUBSCRIPTION_STATUS_LABELS, type StrategySubscriptionRow } from './strategySubscription';
import {
    ActivationCell,
    DeploymentCell,
    PortfolioIdentityCell,
    PositionSizingCell,
    StrategyIdentityCell,
    SubscriptionActionsCell,
    TradingProfileCell
} from './StrategySubscriptionCells';
import StrategySubscriptionDetails from './StrategySubscriptionDetails';
import StrategySubscriptionMobileList from './StrategySubscriptionMobileList';

/**
 * Which side of the subscription each row names. The other side is already named by whatever
 * surrounds the table: a portfolio's settings page, or a strategy's group on the subscriptions page.
 */
export type StrategySubscriptionTableIdentity = 'strategy' | 'portfolio';

/**
 * `panel` fits a narrow settings card and keeps the table down to small screens. `page` has a full
 * page width to fill, so it gives long strategy and profile names more room, and below desktop widths
 * it shows cards, two per row on tablets, instead of a squeezed table.
 */
export type StrategySubscriptionTableLayout = 'panel' | 'page';

interface TableLayoutConfig {
    tableClassName: string;
    cardsClassName: string;
    cardListClassName: string;
    identityColumnSize: number;
    tradingProfileColumnSize: number;
}

// Complete class names, so Tailwind can find every breakpoint variant in the source.
const TABLE_LAYOUTS: Record<StrategySubscriptionTableLayout, TableLayoutConfig> = {
    page: {
        tableClassName: 'hidden lg:block',
        cardsClassName: 'lg:hidden',
        cardListClassName: 'grid items-start gap-3 md:grid-cols-2',
        identityColumnSize: 320,
        tradingProfileColumnSize: 240
    },
    panel: {
        tableClassName: 'hidden sm:block',
        cardsClassName: 'sm:hidden',
        cardListClassName: 'space-y-3',
        identityColumnSize: 210,
        tradingProfileColumnSize: 150
    }
};

/**
 * Fits the switch beside the widest status badge ("Strategy inactive") plus cell padding. Anything
 * narrower lets that badge widen the column, which misaligns tables stacked on the same page.
 */
const STATUS_COLUMN_SIZE = 180;

interface StrategySubscriptionTableProps {
    rows: StrategySubscriptionRow[];
    identity: StrategySubscriptionTableIdentity;
    layout: StrategySubscriptionTableLayout;
    /** Shows the table's own search box. */
    search: boolean;
    onToggleActive: (row: StrategySubscriptionRow) => void;
    onConfigure: (row: StrategySubscriptionRow) => void;
    /** Omitted where subscriptions cannot be removed, which hides the remove button. */
    onRemove?: (row: StrategySubscriptionRow) => void;
}

const IDENTITY_COLUMNS: Record<StrategySubscriptionTableIdentity, ColumnDef<StrategySubscriptionRow>> = {
    portfolio: {
        id: 'portfolio',
        header: 'Portfolio',
        accessorFn: (row) => row.portfolio.name,
        cell: ({ row }) => <PortfolioIdentityCell row={row.original} />
    },
    strategy: {
        id: 'strategy',
        header: 'Strategy',
        accessorFn: (row) => row.strategy.name,
        cell: ({ row }) => <StrategyIdentityCell row={row.original} />
    }
};

const renderPortfolioIdentity = (row: StrategySubscriptionRow) => <PortfolioIdentityCell row={row} />;

/** Subscription rows as a table on wider screens and as cards on phones, with an expandable detail panel. */
const StrategySubscriptionTable = ({
    rows,
    identity,
    layout,
    search,
    onToggleActive,
    onConfigure,
    onRemove
}: StrategySubscriptionTableProps) => {
    const navigate = useNavigate();
    const layoutConfig = TABLE_LAYOUTS[layout];
    const { strategiesById: optionBuyingStrategiesById } = useOptionBuyingStrategies();

    const handleManageScorecards = useCallback(
        (row: StrategySubscriptionRow) => {
            void navigate(`/user/scorecards?strategyId=${row.strategy.id}`);
        },
        [navigate]
    );

    const columns = useMemo<ColumnDef<StrategySubscriptionRow>[]>(
        () => [
            {
                id: 'status',
                header: 'Status',
                accessorFn: (row) => STRATEGY_SUBSCRIPTION_STATUS_LABELS[row.readiness.status],
                size: STATUS_COLUMN_SIZE,
                cell: ({ row }) => <ActivationCell row={row.original} onToggle={onToggleActive} />
            },
            { ...IDENTITY_COLUMNS[identity], size: layoutConfig.identityColumnSize },
            {
                id: 'tradingProfile',
                header: 'Trading profile',
                accessorFn: (row) => row.tradingProfileName ?? 'Not linked',
                size: layoutConfig.tradingProfileColumnSize,
                cell: ({ row }) => <TradingProfileCell row={row.original} onConfigure={onConfigure} />
            },
            {
                id: 'positionSizing',
                header: 'Position sizing',
                accessorFn: (row) => row.sizing.summary,
                size: 190,
                cell: ({ row }) => <PositionSizingCell row={row.original} />
            },
            {
                id: 'deployment',
                header: 'Entry eligibility',
                enableSorting: false,
                size: 120,
                cell: ({ row }) => <DeploymentCell row={row.original} />
            },
            {
                id: 'actions',
                header: '',
                enableSorting: false,
                size: 130,
                meta: { className: 'text-right', headerClassName: 'text-right', sticky: 'right' },
                cell: ({ row }) => (
                    <SubscriptionActionsCell
                        row={row.original}
                        onConfigure={onConfigure}
                        onManageScorecards={handleManageScorecards}
                        onRemove={onRemove}
                    />
                )
            }
        ],
        [handleManageScorecards, identity, layoutConfig, onConfigure, onRemove, onToggleActive]
    );

    return (
        <>
            <div className={layoutConfig.tableClassName}>
                <DataTable
                    columns={columns}
                    data={rows}
                    getRowId={(row) => row.key}
                    search={search}
                    striped={false}
                    renderSubComponent={({ row }) => (
                        <StrategySubscriptionDetails
                            openPositionCount={row.original.openPositionCount}
                            optionBuyingStrategiesById={optionBuyingStrategiesById}
                            readiness={row.original.readiness}
                            sizing={row.original.sizing}
                            strategy={row.original.strategy}
                            tradingProfileName={row.original.tradingProfileName}
                            onConfigure={() => onConfigure(row.original)}
                        />
                    )}
                />
            </div>

            <div className={layoutConfig.cardsClassName}>
                <StrategySubscriptionMobileList
                    className={layoutConfig.cardListClassName}
                    optionBuyingStrategiesById={optionBuyingStrategiesById}
                    renderIdentity={identity === 'portfolio' ? renderPortfolioIdentity : undefined}
                    rows={rows}
                    onConfigure={onConfigure}
                    onManageScorecards={handleManageScorecards}
                    onRemove={onRemove}
                    onToggleActive={onToggleActive}
                />
            </div>
        </>
    );
};

export default StrategySubscriptionTable;
